import { css } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useCallback, useRef } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useIntl } from 'react-intl';
import { QUESTIONNAIRE_AGREEMENT_FIELD_ID, values } from 'sdk';

import { useSDK } from '../../../app';
import { commonMessages } from '../../../i18n/commonMessages';
import { styled } from '../../../styled';
import { useTheme } from '../../../theme/useTheme';
import { CustomRenderThumbVertical } from '../../customRenderThumbVertical';
import { QuestionnaireButton } from '../../questionnaire/button';
import { useAgreement } from '../fields/checkbox/hooks/useAgreement';
import { QuestionnaireCheckbox } from '../fields/checkbox/questionnaireCheckbox';
import { Tooltip } from '../fields/errorLabel';
import { Form } from '../form';
import { getFormValues } from '../utils/getFormValues';

const styles = css`
	box-sizing: border-box;
	display: flex;
	flex-flow: column;
	height: 100%;

	& .wrapper {
		box-sizing: border-box;
		flex-grow: 1;

		& form {
			padding: 20px 16px 0 16px;
		}
	}
`;

const bottomContainerStyles = {
	alignItems: 'flex-start',
	display: 'flex',
	flexWrap: 'wrap',
	padding: '0 16px',
	// flexDirection: 'column-reverse',
	position: 'relative',
} as const;

const checkboxContainer = { position: 'relative', width: '100%' } as const;

export const QuestionnaireForm = styled(
	observer(({ className }: { className: string }): ReactElement => {
		const theme = useTheme();
		const rootStore = useSDK();
		const personalAgreementError = rootStore.formsStore.questionnaireFormErrors[QUESTIONNAIRE_AGREEMENT_FIELD_ID];
		const formRef = useRef<HTMLFormElement>(null);
		const { formatMessage } = useIntl();
		const {
			component: checkboxLabel,
			hook: [checkboxChecked, setCheckboxChecked],
		} = useAgreement(
			rootStore.welcomeDataStore.data?.showcase.questionaryAcceptQuestion || '',
			theme.questionnaireField.checkboxes.questionnaireAgreement.labelLinkColor || '',
			theme.questionnaireField.checkboxes.questionnaireAgreement.labelColor || ''
		);
		const onSend = useCallback(() => {
			if (formRef.current) {
				const values = getFormValues(formRef.current);
				rootStore.sendQuestionnaire(values, checkboxChecked);
			}
		}, [rootStore, checkboxChecked]);
		return (
			<div className={className}>
				<Scrollbars className="wrapper" renderThumbVertical={CustomRenderThumbVertical}>
					<Form
						formRef={formRef}
						errors={rootStore.formsStore.questionnaireFormErrors}
						values={values(rootStore.formsStore.questionnaireFields)}
					/>
				</Scrollbars>
				<div
					css={css`
						div& {
							${bottomContainerStyles}
						}
					`}
				>
					{checkboxLabel && (
						<div
							css={css`
								div& {
									${checkboxContainer}
								}
							`}
						>
							{personalAgreementError && !checkboxChecked && <Tooltip>{personalAgreementError}</Tooltip>}
							<QuestionnaireCheckbox label={checkboxLabel} onChange={setCheckboxChecked} />
						</div>
					)}
					<QuestionnaireButton onClick={onSend} disabled={false}>
						{formatMessage(commonMessages.send)}
					</QuestionnaireButton>
				</div>
			</div>
		);
	})
)(styles);
